import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { track } from 'shared/util/analytics/track';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { Config } from './types';
import { Button, Link, Menu, MenuItem } from '@mui/material';
import {
  ArrowDropDown,
  MoreVert,
  ChevronRight,
  Check,
} from '@material-ui/icons';
import styled from 'styled-components';
import {
  doSignInWithLocationAndIdp,
  doSignInWithPostLoginPageAndIdp,
  getUserManager,
} from 'client/util/userManager';
import { useLocation } from 'react-router-dom';
import { PaddingData } from 'builder/components/WidgetConfigurations/Padding';
import { BackgroundData } from 'builder/components/WidgetConfigurations/Background/Background';
import { User } from 'shared/state/misc/oidc';
import { getAvailableThemes } from 'builder/util/resolveTheme';
// token application handled via Redux thunk from container

const ButtonWrapper = styled.div<{
  width: number;
  height: number;
  linkColor: string;
  fontSize: number;
  font: string;
  signInType: string;
  buttonSolidColor: string;
  buttonGradientColor: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikeThrough: boolean;
  borderData: BorderData;
  paddingData: PaddingData;
  backgroundData: BackgroundData;
  background: string;
  backgroundImage: Image;
  lineColor: string;
  loggedInUser: User | undefined;
}>`
  border-width: ${p => p.borderData?.thickness}px;
  border-style: solid;
  border-color: ${p => p.lineColor};
  border-radius: ${p => p.borderData?.radius}px;
  background-clip: content-box;
  padding: ${p => p.paddingData?.top}px ${p => p.paddingData?.right}px
    ${p => p.paddingData?.bottom}px ${p => p.paddingData?.left}px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background: ${p => p.background};
  background-image: ${p =>
    p.backgroundData?.style === 'Image'
      ? `url('${p.backgroundImage?.url}')`
      : ''};
  margin-left: ${p => (p.loggedInUser ? '8px' : '0')};
  & #options-button {
    background-color: ${p =>
      p.signInType === 'button' ? p.buttonSolidColor : 'transparent'};
    background-image: ${p =>
      p.signInType === 'button' ? p.buttonGradientColor : 'none'};
    margin: 15px 0;
    font-family: ${p => p.font};
    font-size: ${p => p.fontSize}px;
    text-transform: none;
    width: 100%;
    height: ${p => p.height - 30}px;
    color: ${p => p.linkColor};
    font-weight: ${p => p.bold && '700'};
    font-style: ${p => p.italic && 'italic'};
    text-decoration: ${p => p.underline && 'underline'}
      ${p => (p.underline && p.strikeThrough ? ' ' : '')}
      ${p => p.strikeThrough && 'line-through'};
    word-break: break-word;
  }
`;

const StyledMenu = styled(Menu)<{
  linkColor: string;
  fontSize: number;
  font: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikeThrough: boolean;
  borderData: BorderData;
  paddingData: PaddingData;
  dropdownItemsBackgroundColor: string;
  lineColor: string;
}>`
  border-width: ${p => p.borderData?.thickness}px;
  border-style: solid;
  border-color: ${p => p.lineColor};
  border-radius: ${p => p.borderData?.radius}px;
  background-clip: content-box;
  padding-top: ${p => p.paddingData?.top}px;
  padding-right: ${p => p.paddingData?.right}px;
  padding-bottom: ${p => p.paddingData?.bottom}px;
  padding-left: ${p => p.paddingData?.left}px;
  & div:has(ul) {
    background: ${p => p.dropdownItemsBackgroundColor};
  }
  & ul[aria-labelledby^='options-button'] > li {
    font-weight: ${p => p.bold && '700'};
    font-style: ${p => p.italic && 'italic'};
    font-family: ${p => p.font};
    font-size: ${p => p.fontSize}px;
    color: ${p => p.linkColor};
    text-decoration: ${p => p.underline && 'underline'}
      ${p => (p.underline && p.strikeThrough ? ' ' : '')}
      ${p => p.strikeThrough && 'line-through'};
    & > a {
      text-decoration: ${p =>
          p.underline && !p.strikeThrough ? 'underline' : 'none'}
        ${p => (p.underline && p.strikeThrough ? ' ' : '')}
        ${p => p.strikeThrough && 'line-through'};
      color: ${p => p.linkColor};
      font-weight: ${p => p.bold && '700'};
      font-style: ${p => p.italic && 'italic'};
    }
  }
`;

const StyledMenuItem = styled(MenuItem)<{
  dropdownItemsBackgroundColor: string;
}>`
  background-color: ${p => p.dropdownItemsBackgroundColor};
  &:hover {
    background-color: ${p => p.dropdownItemsBackgroundColor};
  }
`;

const StyledSubMenu = styled(Menu)<{
  dropdownItemsBackgroundColor: string;
  lineColor: string;
  textColor: string;
  hoverBg: string;
}>`
  & .MuiPaper-root {
    background: ${p => p.dropdownItemsBackgroundColor};
    border: 1px solid ${p => p.lineColor};
    border-radius: 4px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    min-width: 148px;
    color: ${p => p.textColor};
  }
  & .MuiMenuItem-root {
    color: ${p => p.textColor};
  }
  & .MuiMenuItem-root:hover {
    background-color: ${p => p.hoverBg};
  }
`;

// Determine if a color is dark to choose contrasting text color
const isColorDark = (color: string): boolean => {
  if (!color) return false;
  let r = 255,
    g = 255,
    b = 255;
  const c = color.trim();
  // Hex formats: #RGB, #RRGGBB
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      return false;
    }
  } else if (c.startsWith('rgb')) {
    const match = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return false;
    }
  } else {
    // For named colors or others, default to not-dark
    return false;
  }
  // Perceived luminance (ITU-R BT.709)
  const luminance =
    0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);
  return luminance < 0.5;
};

type RGB = { r: number; g: number; b: number };
const parseToRgb = (color: string): RGB | null => {
  if (!color) return null;
  const c = color.trim();
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return null;
  }
  if (c.startsWith('rgb')) {
    const match = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (match) {
      return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10),
      };
    }
    return null;
  }
  return null;
};

const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
const toRgbString = ({ r, g, b }: RGB) =>
  `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;

const lightenColor = (color: string, amount = 0.12): string => {
  const rgb = parseToRgb(color);
  if (!rgb) return 'rgba(255,255,255,0.10)';
  return toRgbString({
    r: rgb.r + (255 - rgb.r) * amount,
    g: rgb.g + (255 - rgb.g) * amount,
    b: rgb.b + (255 - rgb.b) * amount,
  });
};

const darkenColor = (color: string, amount = 0.06): string => {
  const rgb = parseToRgb(color);
  if (!rgb) return 'rgba(0,0,0,0.06)';
  return toRgbString({
    r: rgb.r * (1 - amount),
    g: rgb.g * (1 - amount),
    b: rgb.b * (1 - amount),
  });
};

export interface LoginButtonProps {
  initiateLogout: () => void;
  applyThemeToPages?: (
    themeKey: string,
    opts?: { scope?: 'all' | 'current'; persistEnabledKey?: boolean }
  ) => void;
}

export const LoginButtonUnwrapped: React.FC<WidgetRenderProps<Config> &
  LoginButtonProps> = (
  props: WidgetRenderProps<Config> & LoginButtonProps
): ReactElement => {
  const {
    signInIcon,
    signInType,
    signInOptionEnabled,
    signInOptionLinks,
    signInOptionTitle,
    width,
    height,
    signInLinkColor,
    font,
    fontSize,
    buttonColor,
    bold,
    italic,
    underline,
    strikeThrough,
    borderData,
    paddingData,
    backgroundData,
    backgroundImage,
    lineColor,
    simulateLoggedOffUser,
    visible,
    isCustomWidget,
    profileInLoginDropdownEnabled,
    themeSwitcherDropdownEnabled,
  } = props.widgetState.config;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchorEl);
  const [loggedInUser, setRealUser] = React.useState<User | undefined>(
    undefined
  );
  const [
    themeMenuAnchorEl,
    setThemeMenuAnchorEl,
  ] = useState<null | HTMLElement>(null);
  const themeMenuOpen = Boolean(themeMenuAnchorEl);
  const [isMouseOverThemeTrigger, setIsMouseOverThemeTrigger] = useState(false);
  const [isMouseOverThemeSubmenu, setIsMouseOverThemeSubmenu] = useState(false);
  const [isKeyboardActivated, setIsKeyboardActivated] = useState(false);
  const [hoverTimerId, setHoverTimerId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const location = useLocation();

  const getIsLiveSite = () => {
    const urlSegments = location.pathname.split('/');
    return !props.editing && urlSegments[urlSegments.length - 2] !== 'preview';
  };

  const isLiveSite = getIsLiveSite();

  useEffect(() => {
    if (simulateLoggedOffUser && loggedInUser !== undefined) {
      setRealUser(undefined);
    }
    if (!simulateLoggedOffUser || isLiveSite) {
      setRealUser(props.user);
    }
  }, [simulateLoggedOffUser, props.user, isLiveSite]);

  const handleMenuClick = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (!props.editing) {
      setMenuAnchorEl(e.currentTarget);
      trackLogin('login_widget_menu_open', {
        authenticated: !!loggedInUser,
        widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
      });
    }
  };

  const handleButtonClick = (target: SignInLink['type']) => {
    const slug = location.pathname;
    if (
      slug.includes(props.currentPage?.pageId.toString()) ||
      slug.includes('preview')
    ) {
      return;
    } else {
      const currentPage = props.currentPage;

      currentPage && currentPage.authentication
        ? doSignInWithLocationAndIdp(location, target)
        : doSignInWithPostLoginPageAndIdp(location, target);
      if (!props.editing) {
        trackLogin('login_widget_signin_click', {
          idpTarget: target,
          authenticated: !!loggedInUser,
          widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
        });
      }
    }
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setThemeMenuAnchorEl(null);
    setIsMouseOverThemeTrigger(false);
    setIsMouseOverThemeSubmenu(false);
    setIsKeyboardActivated(false);
  };

  useEffect(() => {
    if (
      isMouseOverThemeTrigger ||
      isMouseOverThemeSubmenu ||
      isKeyboardActivated
    ) {
      if (!themeMenuOpen && (isMouseOverThemeTrigger || isKeyboardActivated)) {
        const timer = setTimeout(() => {
          // open submenu when hovering the trigger by anchoring to the trigger element
          if (menuAnchorEl) {
            // themeMenuAnchorEl should be the theme trigger item, tracked during mouse enter
            setThemeMenuAnchorEl(prev => prev ?? (menuAnchorEl as HTMLElement));
          }
        }, 350);
        setHoverTimerId(timer);
      }
    } else {
      setThemeMenuAnchorEl(null);
    }

    return () => {
      if (hoverTimerId) clearTimeout(hoverTimerId);
    };
  }, [isMouseOverThemeTrigger, isMouseOverThemeSubmenu, isKeyboardActivated]);

  const handleButtonMenuClick = (
    target: SignInLink['type'],
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (signInOptionEnabled === 'enabled') {
      setMenuAnchorEl(e.currentTarget);
      if (!props.editing) {
        trackLogin('login_widget_menu_open', {
          authenticated: !!loggedInUser,
          widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
        });
      }
    } else {
      if (target !== 'custom') {
        const slug = location.pathname;
        if (
          slug.includes(props.currentPage?.pageId.toString()) ||
          slug.includes('preview')
        ) {
          return;
        } else {
          location.state = {};
          const currentPage = props.currentPage;
          currentPage && currentPage.authentication
            ? doSignInWithLocationAndIdp(location, target)
            : doSignInWithPostLoginPageAndIdp(location, target);
          if (!props.editing) {
            trackLogin('login_widget_signin_click', {
              idpTarget: target,
              authenticated: !!loggedInUser,
              widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
            });
          }
        }
      } else {
        return;
      }
    }
  };

  const getRedirectUri = () => {
    const domain = props.site?.domain;
    const fallbackUri = domain
      ? `https://${domain}/home`
      : `${globalThis.location.origin}/home`;
    const postLogoutPage = props.site?.postLogoutPage;
    const availablePages = props.pages || [];

    if (!postLogoutPage) {
      return fallbackUri;
    }

    if (postLogoutPage.type === 'url' && postLogoutPage.url?.trim()) {
      return postLogoutPage.url;
    }

    if (postLogoutPage.type === 'page' && postLogoutPage.pageId !== undefined) {
      const postLogoutSlug = availablePages.find(
        page => page.current?.pageId === postLogoutPage.pageId
      )?.current?.slug;

      if (postLogoutSlug) {
        return domain
          ? `https://${domain}/${postLogoutSlug}`
          : `${globalThis.location.origin}/${postLogoutSlug}`;
      } else {
        console.warn(
          'Post logout pageId not found in available pages. Falling back to /home.'
        );
      }
    }

    return fallbackUri;
  };

  // Submenu text color based on submenu background shade
  const submenuBg =
    props.widgetState.config.dropdownItemsBackgroundColor ?? '#ffffff';
  // Available themes and selection derived from config/site
  const availableThemes = useMemo(() => getAvailableThemes(props.site), [
    props.site,
  ]);
  const configuredThemeKeys: string[] = useMemo(
    () =>
      Array.isArray(props.widgetState.config.themeSwitcherOptions)
        ? props.widgetState.config.themeSwitcherOptions.map(k => String(k))
        : [],
    [props.widgetState.config.themeSwitcherOptions]
  );
  const derivedSelectedThemeOptions = useMemo(() => {
    if (configuredThemeKeys.length > 0) {
      return availableThemes.filter(t => configuredThemeKeys.includes(t.key));
    }
    const enabled = String(props.site?.theme?.enabledTheme || '');
    const match = availableThemes.find(t => t.key === enabled);
    return match ? [match] : [];
  }, [availableThemes, configuredThemeKeys, props.site?.theme?.enabledTheme]);
  const enabledKey = String(props.site?.theme?.enabledTheme || '');
  const preferredDefaultKey = useMemo(() => {
    if (
      enabledKey &&
      derivedSelectedThemeOptions.some(t => t.key === enabledKey)
    ) {
      return enabledKey;
    }
    return derivedSelectedThemeOptions[0]?.key || enabledKey || '';
  }, [enabledKey, derivedSelectedThemeOptions]);
  const [activeThemeKey, setActiveThemeKey] = useState<string>(
    preferredDefaultKey
  );
  useEffect(() => {
    // keep active key in sync if selection changes or becomes invalid; prefer enabledKey when available
    if (
      !activeThemeKey ||
      !derivedSelectedThemeOptions.some(t => t.key === activeThemeKey)
    ) {
      setActiveThemeKey(preferredDefaultKey);
    }
  }, [preferredDefaultKey, derivedSelectedThemeOptions]);

  const selectedThemeForStyling = useMemo(
    () =>
      derivedSelectedThemeOptions.find(t => t.key === activeThemeKey) ||
      derivedSelectedThemeOptions[0],
    [derivedSelectedThemeOptions, activeThemeKey]
  );
  const preferredTextColor =
    selectedThemeForStyling?.theme?.colors?.text?.primary || signInLinkColor;
  const submenuTextColor = useMemo(
    () => (isColorDark(submenuBg) ? '#ffffff' : preferredTextColor),
    [submenuBg, preferredTextColor]
  );
  const submenuHoverBg = useMemo(
    () =>
      isColorDark(submenuBg)
        ? lightenColor(submenuBg, 0.12)
        : darkenColor(submenuBg, 0.06),
    [submenuBg]
  );

  // Theme application is delegated to props.applyThemeToPages (Redux thunk)

  const handleLogout = () => {
    if (loggedInUser && !props.editing) {
      props.initiateLogout();
      getUserManager().signoutRedirect({
        post_logout_redirect_uri: getRedirectUri(),
      });
      trackLogin('login_widget_logout', {
        authenticated: true,
        userName: loggedInUser.profile.name,
        widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
      });
    } else {
      return;
    }
  };

  const menuItems = () => {
    if (!loggedInUser) {
      if (signInOptionEnabled === 'enabled') {
        return [
          <StyledMenuItem
            key={'default-item'}
            data-test-id="default-menu-item"
            disabled
            disableRipple
            dropdownItemsBackgroundColor={
              props.widgetState.config.dropdownItemsBackgroundColor ?? '#ffffff'
            }
          >
            {signInOptionTitle}
          </StyledMenuItem>,
          ...(signInOptionLinks?.length
            ? signInOptionLinks.map((link, index) => (
                <StyledMenuItem
                  key={`${link.label}${index}`}
                  dropdownItemsBackgroundColor={
                    props.widgetState.config.dropdownItemsBackgroundColor ??
                    '#ffffff'
                  }
                >
                  <Link
                    data-test-id="menu-item"
                    href={link.type === 'custom' && link.url}
                    target={link.openInNewTab && '_blank'}
                    rel="noopener"
                    onClick={() => {
                      handleButtonClick(link.type);
                      if (!props.editing) {
                        trackLogin('login_widget_dropdown_item_click', {
                          itemLabel: link.label,
                          itemType: link.type,
                          authenticated: !!loggedInUser,
                          widgetAnalyticsLabel:
                            props.widgetState.config.analyticsLabel,
                        });
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </StyledMenuItem>
              ))
            : []),
        ];
      } else {
        return [];
      }
    } else {
      // Use precomputed theme selection values
      const selectedThemeOptions = derivedSelectedThemeOptions;
      return [
        profileInLoginDropdownEnabled === 'enabled' && (
          <StyledMenuItem
            key={'profile-item'}
            dropdownItemsBackgroundColor={
              props.widgetState.config.dropdownItemsBackgroundColor ?? '#ffffff'
            }
          >
            <Link
              data-test-id="menu-item"
              href={`https://${props.site?.domain}/profile`}
              target="_blank"
              rel="noopener"
            >
              Profile
            </Link>
          </StyledMenuItem>
        ),
        ...(themeSwitcherDropdownEnabled === 'enabled' &&
        selectedThemeOptions.length > 0
          ? [
              <StyledMenuItem
                key={'theme-switcher-item'}
                data-test-id="theme-dropdown"
                onMouseEnter={e => {
                  setIsMouseOverThemeTrigger(true);
                  setThemeMenuAnchorEl(e.currentTarget as HTMLElement);
                }}
                onMouseLeave={() => setIsMouseOverThemeTrigger(false)}
                onClick={e => {
                  setIsKeyboardActivated(!themeMenuOpen);
                  setThemeMenuAnchorEl(e.currentTarget as HTMLElement);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsKeyboardActivated(!themeMenuOpen);
                  }
                }}
                dropdownItemsBackgroundColor={
                  props.widgetState.config.dropdownItemsBackgroundColor ??
                  '#ffffff'
                }
              >
                <span style={{ flex: 1 }}>Theme</span>
                <ChevronRight fontSize="small" />
              </StyledMenuItem>,
              <StyledSubMenu
                key={'theme-submenu'}
                anchorEl={themeMenuAnchorEl}
                open={themeMenuOpen}
                onClose={() => setThemeMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ 'data-test-id': 'theme-submenu' }}
                dropdownItemsBackgroundColor={
                  props.widgetState.config.dropdownItemsBackgroundColor ??
                  '#ffffff'
                }
                lineColor={lineColor}
                textColor={submenuTextColor}
                hoverBg={submenuHoverBg}
                onMouseEnter={() => setIsMouseOverThemeSubmenu(true)}
                onMouseLeave={() => setIsMouseOverThemeSubmenu(false)}
              >
                {selectedThemeOptions.map(opt => (
                  <MenuItem
                    key={opt.key}
                    data-test-id="theme-option-item"
                    disableRipple
                    onClick={() => {
                      setActiveThemeKey(opt.key);
                      props.applyThemeToPages?.(opt.key, {
                        scope: 'all',
                        persistEnabledKey: true,
                      });
                    }}
                  >
                    <span style={{ flex: 1 }}>{opt.name}</span>
                    {opt.key === activeThemeKey && (
                      <Check
                        data-test-id="selected-theme-option"
                        fontSize="small"
                      />
                    )}
                  </MenuItem>
                ))}
              </StyledSubMenu>,
            ]
          : []),
        <StyledMenuItem
          key={'sign-out-item'}
          onClick={() => handleLogout()}
          dropdownItemsBackgroundColor={
            props.widgetState.config.dropdownItemsBackgroundColor ?? '#ffffff'
          }
        >
          Sign Out
        </StyledMenuItem>,
      ];
    }
  };

  /* doesn't actually update "width" property, but helps ensure proper rendering if user
      has a long name that goes beyond the width of the widget */
  const actualWidth = () => {
    if (signInType === 'button') {
      if (loggedInUser) {
        return width + (loggedInUser?.profile.name.length * fontSize) / 2;
      } else {
        return width;
      }
    }
    return width;
  };

  function background() {
    if (backgroundData.style === 'Color') {
      return backgroundData?.color;
    }
    if (backgroundData.style === 'Image') {
      return `url('${backgroundImage?.url}')`;
    }
    return 'transparent';
  }

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  const trackLogin = useTrackWidget({
    tracking: (props as any).tracking,
    widgetState: props.widgetState,
    currentPage: props.currentPage,
    site: props.site,
  });

  return (
    <>
      <ButtonWrapper
        data-test-id="login-button-widget"
        borderData={borderData}
        backgroundData={backgroundData}
        background={background()}
        paddingData={paddingData}
        backgroundImage={backgroundImage}
        bold={bold}
        italic={italic}
        underline={underline}
        strikeThrough={strikeThrough}
        buttonSolidColor={
          loggedInUser
            ? 'transparent'
            : buttonColor.startsWith('#') || buttonColor.startsWith('rgb')
            ? buttonColor
            : 'transparent'
        }
        buttonGradientColor={
          loggedInUser
            ? 'transparent'
            : buttonColor.startsWith('#') || buttonColor.startsWith('rgb')
            ? 'none'
            : buttonColor
        }
        signInType={signInType}
        width={actualWidth()}
        height={height}
        linkColor={signInLinkColor}
        font={font}
        fontSize={fontSize}
        lineColor={lineColor}
        loggedInUser={loggedInUser}
      >
        {!loggedInUser ? (
          <Button
            data-test-id="login-button"
            disableRipple
            id="options-button"
            variant={'text'}
            color="primary"
            endIcon={
              signInOptionEnabled === 'enabled' ? (
                signInIcon === 'MoreVertical' ? (
                  <MoreVert width={`${fontSize}px`} />
                ) : (
                  <ArrowDropDown width={`${fontSize}px`} />
                )
              ) : (
                undefined
              )
            }
            aria-controls={open ? 'options-menu' : undefined}
            aria-haspopup="true"
            disableElevation
            aria-expanded={open ? 'true' : undefined}
            onClick={e =>
              handleButtonMenuClick(props.site?.idpHint || 'portal-login', e)
            }
          >
            Sign In
          </Button>
        ) : (
          <Button
            data-test-id="user-button"
            disableRipple
            id="options-button"
            variant={signInType === 'button' ? 'contained' : 'text'}
            color="primary"
            endIcon={
              signInIcon === 'MoreVertical' ? (
                <MoreVert width={`${fontSize}px`} />
              ) : (
                <ArrowDropDown width={`${fontSize}px`} />
              )
            }
            aria-controls={open ? 'options-menu' : undefined}
            aria-haspopup="true"
            disableElevation
            aria-expanded={open ? 'true' : undefined}
            onClick={e => {
              handleMenuClick(e);
              if (!props.editing) {
                trackLogin('login_widget_user_button_click', {
                  authenticated: !!loggedInUser,
                  userName: loggedInUser?.profile.name,
                  widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
                });
              }
            }}
          >
            {loggedInUser?.profile.name}
          </Button>
        )}
      </ButtonWrapper>
      <StyledMenu
        data-test-id="login-button-menu"
        borderData={borderData}
        paddingData={paddingData}
        dropdownItemsBackgroundColor={
          props.widgetState.config.dropdownItemsBackgroundColor ?? '#ffffff'
        }
        bold={bold}
        italic={italic}
        underline={underline}
        strikeThrough={strikeThrough}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        linkColor={signInLinkColor}
        font={font}
        fontSize={fontSize}
        id="options-menu"
        open={open}
        MenuListProps={{ 'aria-labelledby': 'options-button' }}
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        lineColor={lineColor}
      >
        {menuItems()}
      </StyledMenu>
    </>
  );
};
export default track()(LoginButtonUnwrapped);
